import axios, { type AxiosHeaders, type Method } from 'axios';
import { API_URL } from '@/globals';
import i18n from '@/i18n/i18n';
import { getApiErrorMessage } from './apiError';

declare module 'axios' {
  export interface AxiosRequestConfig {
    retried?: boolean;
    skipAuthRefresh?: boolean;
    skipErrorToast?: boolean;
  }
}

export const API_ERROR_EVENT = 'api:error';

const trimTrailingSlash = (url: string | undefined) => {
  if (!url)
    throw new Error(
      'API_URL is not configured. Set VITE_API_URL in your .env.local file.',
    );
  return url.endsWith('/') ? url.slice(0, -1) : url;
};

const trimLeadingSlash = (url: string) =>
  url.startsWith('/') ? url.slice(1) : url;

const axiosInstance = axios.create();

let isRefreshing = false;
let pendingRequests: {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}[] = [];

const notifyApiError = (error: unknown) => {
  window.dispatchEvent(
    new CustomEvent(API_ERROR_EVENT, {
      detail: { message: getApiErrorMessage(error) },
    }),
  );
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config as typeof error.config;

    if (
      config.skipAuthRefresh ||
      error.response?.status !== 401 ||
      config.retried
    ) {
      if (error.response?.status !== 401 && !config.skipErrorToast) {
        notifyApiError(error);
      }
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem('refresh_token');

    if (!refreshToken) {
      localStorage.removeItem('access_token');
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        pendingRequests.push({ resolve, reject });
      }).then((token) => {
        config.headers.Authorization = `Bearer ${token}`;
        return axiosInstance(config);
      });
    }

    config.retried = true;
    isRefreshing = true;

    try {
      const { data } = await axios.post(
        `${trimTrailingSlash(API_URL)}/v1/auth/refresh`,
        undefined,
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
          },
        },
      );

      const newToken: string = data.access_token;
      localStorage.setItem('access_token', newToken);
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }

      pendingRequests.forEach((p) => p.resolve(newToken));
      pendingRequests = [];

      config.headers.Authorization = `Bearer ${newToken}`;
      return axiosInstance(config);
    } catch {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      pendingRequests.forEach((p) => p.reject(error));
      pendingRequests = [];
      window.dispatchEvent(new CustomEvent('auth:session-expired'));
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  },
);

type RequestOptions = {
  url?: string;
  endpoint?: string;
  method?: Method;
  headers?: AxiosHeaders | Record<string, string>;
  params?: Record<string, any>;
  body?: any;
  signal?: AbortSignal;
  skipAuthRefresh?: boolean;
  skipErrorToast?: boolean;
  locale?: string;
};

export const apiRequest = async <T>({
  url,
  endpoint,
  method,
  headers,
  body: data,
  params,
  signal,
  skipAuthRefresh,
  skipErrorToast,
  locale: localeOverride,
}: RequestOptions = {}): Promise<T> => {
  const normalizedUrl = endpoint
    ? `${trimTrailingSlash(API_URL)}/${trimLeadingSlash(endpoint)}`
    : url;

  const token =
    localStorage.getItem('access_token') ?? import.meta.env.VITE_API_TOKEN;
  const locale = (
    localeOverride ??
    i18n.language ??
    i18n.resolvedLanguage ??
    'en'
  ).split('-')[0];

  const finalHeaders = {
    Accept: 'application/json',
    'X-Locale': locale,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(headers ?? {}),
  };

  const response = await axiosInstance.request<T>({
    url: normalizedUrl,
    method: method || (data ? 'POST' : 'GET'),
    headers: finalHeaders,
    params,
    data,
    signal,
    skipAuthRefresh,
    skipErrorToast,
  });

  return response.data;
};
